<template>
    <div>
        <v-card class="mb-4">
            <v-card-title>
                <v-icon class="mr-2">mdi-filter</v-icon>
                Filtros de Busca
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-checkbox
                                    v-model="usarFiltroPadrao"
                                    label="Usar filtro padrão (QD)"
                                    @change="onFiltroChange"
                                ></v-checkbox>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-checkbox
                                    v-model="usarFiltroNumeros"
                                    label="Filtrar por números de título"
                                    @change="onFiltroChange"
                                ></v-checkbox>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>

                <v-row v-if="usarFiltroNumeros">
                    <v-col cols="12">
                        <v-textarea
                            v-model="numerosTitulos"
                            label="Números de Títulos"
                            placeholder="Digite os números dos títulos separados por vírgula, enter ou espaço"
                            hint="Exemplo: 477977/1, 721603/6, 755202/5"
                            rows="4"
                            outlined
                        >
                            <template v-slot:append>
                                <v-icon
                                    @click="limparNumerosTitulos"
                                    class="v-icon--clearable"
                                >
                                    mdi-close-circle
                                </v-icon>
                            </template>
                        </v-textarea>
                    </v-col>
                </v-row>

                <v-row v-if="usarFiltroPadrao && usarFiltroNumeros">
                    <v-col cols="12">
                        <v-alert type="info" outlined dense>
                            <strong>Filtros combinados:</strong> Serão buscados
                            títulos que contenham QD na revisão OU que estejam
                            na lista de números informados com status 220
                            (Solicitado protesto).
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row v-else-if="usarFiltroPadrao">
                    <v-col cols="12">
                        <v-alert type="info" outlined dense>
                            <strong>Filtro padrão:</strong> Serão buscados
                            apenas títulos que contenham [QD] na revisão.
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row v-else-if="usarFiltroNumeros">
                    <v-col cols="12">
                        <v-alert type="warning" outlined dense>
                            <strong>Filtro por números:</strong> Apenas títulos
                            com status 220 da lista informada serão elegíveis.
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-btn
                            color="primary"
                            @click="buscarTitulos"
                            :loading="loading"
                            :disabled="!podeBuscar"
                        >
                            <v-icon class="mr-2">mdi-magnify</v-icon>
                            Buscar Títulos
                        </v-btn>

                        <v-btn
                            v-if="titulosElegiveis.length > 0"
                            color="success"
                            class="ml-2"
                            @click="baixarModelo"
                            :disabled="titulosSelecionados.length === 0"
                        >
                            <v-icon class="mr-2">mdi-download</v-icon>
                            Baixar modelo de importação de devedores
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-card v-if="titulos.length > 0" class="mt-4">
            <v-card-text>
                <v-row>
                    <v-col cols="12" sm="3">
                        <v-card color="blue" dark>
                            <v-card-text>
                                <div class="text-h6">{{ titulos.length }}</div>
                                <div>Total de Títulos</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="green" dark>
                            <v-card-text>
                                <div class="text-h6">
                                    {{ titulosElegiveis.length }}
                                </div>
                                <div>Títulos Elegíveis</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="orange" dark>
                            <v-card-text>
                                <div class="text-h6">
                                    {{ titulosSelecionados.length }}
                                </div>
                                <div>Selecionados</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="red" dark>
                            <v-card-text>
                                <div class="text-h6">
                                    {{ titulosInelegiveis.length }}
                                </div>
                                <div>Inelegíveis</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-data-table
            v-if="titulos.length > 0"
            :headers="headers"
            :items="titulos"
            v-model="titulosSelecionados"
            item-key="unique_id"
            show-select
            :loading="loading"
            :footer-props="{
                itemsPerPageOptions: [50, 100, 200],
                itemsPerPageText: 'Itens por página:'
            }"
            loading-text="Pesquisando títulos..."
            no-data-text="Nenhum título encontrado..."
            class="elevation-1"
            @toggle-select-all="onToggleSelectAll"
        >
            <template
                v-slot:[`item.data-table-select`]="{ item, isSelected, select }"
            >
                <v-checkbox
                    :input-value="isSelected"
                    :disabled="!item.elegivel"
                    @change="select(!isSelected)"
                    color="primary"
                ></v-checkbox>
            </template>

            <template v-slot:[`item.id_titulo`]="{ item }">
                <span :style="{ color: item.elegivel ? 'black' : 'grey' }">
                    {{ item.id_titulo }}
                </span>
            </template>

            <template v-slot:[`item.ds_nosso_numero`]="{ item }">
                <span :style="{ color: item.elegivel ? 'black' : 'grey' }">
                    {{ item.ds_nosso_numero }}
                </span>
            </template>

            <template v-slot:[`item.devedor_principal`]="{ item }">
                <div :style="{ color: item.elegivel ? 'black' : 'grey' }">
                    <div class="font-weight-bold">
                        {{ item.nm_devedor_principal }}
                    </div>
                    <div class="text-caption">
                        {{ formatarDocumento(item.nu_identificacao_devedor) }}
                    </div>
                </div>
            </template>

            <template v-slot:[`item.qtd_devedores_esperada`]="{ item }">
                <v-chip
                    :color="item.nu_quantidade_devedores > 0 ? 'blue' : 'grey'"
                    text-color="white"
                    small
                >
                    {{ item.nu_quantidade_devedores || 0 }}
                </v-chip>
            </template>

            <template v-slot:[`item.qtd_cadastrada`]="{ item }">
                <v-chip
                    :color="item.qtd_cadastrada > 0 ? 'green' : 'orange'"
                    text-color="white"
                    small
                >
                    {{ item.qtd_cadastrada || 0 }}
                </v-chip>
            </template>

            <template v-slot:[`item.cd_status`]="{ item }">
                <v-chip
                    :color="
                        getStatusColor(item.cd_status, item.ds_revisao, item)
                    "
                    text-color="white"
                    small
                >
                    {{ getStatusText(item.cd_status, item.ds_revisao, item) }}
                </v-chip>
            </template>

            <template v-slot:[`item.aviso`]="{ item }">
                <div v-if="item.mensagem_aviso">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon color="error" v-bind="attrs" v-on="on">
                                mdi-alert-circle
                            </v-icon>
                        </template>
                        <span>{{ item.mensagem_aviso }}</span>
                    </v-tooltip>
                </div>
                <div v-else>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon color="success" v-bind="attrs" v-on="on">
                                mdi-check-circle
                            </v-icon>
                        </template>
                        <span>Título elegível para manutenção em lote</span>
                    </v-tooltip>
                </div>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import Vue from 'vue';
import TituloService from '@/services/TituloService';
import * as XLSX from 'xlsx';

export default {
    name: 'ConsultaDownloadModelo',
    data() {
        return {
            usarFiltroPadrao: true,
            usarFiltroNumeros: false,
            numerosTitulos: '',
            loading: false,
            titulos: [],
            titulosSelecionados: [],
            tituloService: new TituloService(Vue.http, this.$store),
            headers: [
                {
                    text: 'Nº Título',
                    value: 'id_titulo',
                    sortable: false
                },
                {
                    text: 'Nosso Número',
                    value: 'ds_nosso_numero',
                    sortable: false
                },
                {
                    text: 'Devedor Principal',
                    value: 'devedor_principal',
                    sortable: false,
                    width: '250px'
                },
                {
                    text: 'Qtd. Devedores Esperada',
                    value: 'qtd_devedores_esperada',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Qtd. Cadastrada',
                    value: 'qtd_cadastrada',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Status',
                    value: 'cd_status',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Aviso',
                    value: 'aviso',
                    sortable: false,
                    align: 'center'
                }
            ]
        };
    },
    computed: {
        podeBuscar() {
            if (this.usarFiltroPadrao && !this.usarFiltroNumeros) {
                return true;
            }
            if (!this.usarFiltroPadrao && this.usarFiltroNumeros) {
                return (
                    this.numerosTitulos && this.numerosTitulos.trim().length > 0
                );
            }
            if (this.usarFiltroPadrao && this.usarFiltroNumeros) {
                return (
                    this.numerosTitulos && this.numerosTitulos.trim().length > 0
                );
            }
            return false;
        },

        titulosElegiveis() {
            return this.titulos.filter(titulo => titulo.elegivel);
        },

        titulosInelegiveis() {
            return this.titulos.filter(titulo => !titulo.elegivel);
        }
    },
    watch: {
        titulosSelecionados: {
            handler(novos) {
                this.$emit('titulos-selecionados', novos);
            },
            deep: true
        }
    },
    methods: {
        onFiltroChange() {
            if (!this.usarFiltroPadrao && !this.usarFiltroNumeros) {
                this.usarFiltroPadrao = true;
            }
        },

        limparNumerosTitulos() {
            this.numerosTitulos = '';
        },

        async buscarTitulos() {
            if (!this.podeBuscar) return;

            this.loading = true;
            this.titulos = [];
            this.titulosSelecionados = [];

            try {
                let filtros = {};

                if (this.usarFiltroPadrao) {
                    filtros.ds_revisao = 'QD';
                }

                if (this.usarFiltroNumeros) {
                    const numeros = this.processarNumerosTitulos();
                    if (numeros.length === 0) {
                        this.showError(
                            'Nenhum número de título válido encontrado.'
                        );
                        return;
                    }
                    filtros.numeros_titulos = numeros;

                    if (!this.usarFiltroPadrao) {
                        filtros.cd_status = '220';
                    }
                }

                const response = await this.tituloService.buscarManutencao(
                    filtros,
                    1,
                    1000
                );

                if (response && response.body) {
                    const titulos =
                        response.body.results || response.body.titulos || [];
                    this.titulos = this.processarTitulos(titulos);
                }
            } catch (error) {
                console.error('Erro ao buscar títulos:', error);
                this.showError('Erro ao buscar títulos. Tente novamente.');
            } finally {
                this.loading = false;
            }
        },

        processarNumerosTitulos() {
            if (!this.numerosTitulos) return [];

            return this.numerosTitulos
                .split(/[,\n\s]+/)
                .map(num => num.trim())
                .filter(num => num.length > 0);
        },

        processarTitulos(titulos) {
            return titulos.map((titulo, index) => {
                const temAviso = titulo.aviso && titulo.aviso.trim().length > 0;

                const elegivel = !temAviso;

                const devedorPrincipal =
                    titulo.devedor && titulo.devedor.length > 0
                        ? titulo.devedor.find(
                              dev => dev.fl_devedor_principal === true
                          ) || titulo.devedor[0]
                        : null;

                return {
                    ...titulo,
                    unique_id: `${titulo.ds_numero_titulo ||
                        titulo.id_titulo}_${index}_${Date.now()}`,
                    id_titulo: titulo.id_titulo,
                    nm_devedor_principal: devedorPrincipal
                        ? devedorPrincipal.nm_devedor
                        : titulo.nm_devedor || 'N/A',
                    nu_quantidade_devedores:
                        titulo.nu_quantidade_devedores || 0,
                    qtd_cadastrada: titulo.nu_devedores_cadastrados || 0,
                    nu_identificacao_devedor: devedorPrincipal
                        ? devedorPrincipal.nu_identificacao_devedor
                        : titulo.nu_identificacao_devedor,
                    devedor_principal: devedorPrincipal,
                    devedores: titulo.devedor || [],
                    elegivel: elegivel,
                    mensagem_aviso: temAviso ? titulo.aviso : null
                };
            });
        },

        baixarModelo() {
            if (this.titulosSelecionados.length === 0) {
                this.showError('Selecione pelo menos um título elegível.');
                return;
            }

            this.gerarArquivoExcel();
        },

        gerarArquivoExcel() {
            try {
                const dadosPlanilha = this.processarDadosPlanilha();

                const wb = XLSX.utils.book_new();

                const ws = XLSX.utils.aoa_to_sheet(dadosPlanilha);

                ws['!cols'] = [
                    { wch: 12 }, // ID TITULO
                    { wch: 15 }, // NOSSO NUMERO
                    { wch: 15 }, // CD_APRESENTANTE
                    { wch: 25 }, // NOME DEVEDOR
                    { wch: 15 }, // CPF/CNPJ
                    { wch: 30 }, // ENDERECO
                    { wch: 15 }, // BAIRRO
                    { wch: 15 }, // CIDADE
                    { wch: 5 }, // UF
                    { wch: 10 }, // CEP
                    { wch: 25 }, // EMAIL
                    { wch: 15 }, // TELEFONE
                    { wch: 10 } // PRINCIPAL
                ];

                // Aplicar proteção simples apenas nas colunas ID TITULO e NOSSO NUMERO
                //this.aplicarProtecaoSimples(ws);

                XLSX.utils.book_append_sheet(wb, ws, 'Co-devedores');

                const nomeArquivo = this.gerarNomeArquivo();

                try {
                    XLSX.writeFile(wb, nomeArquivo);
                } catch (writeError) {
                    console.warn(
                        'Método writeFile falhou, tentando download manual:',
                        writeError
                    );
                    const wbout = XLSX.write(wb, {
                        bookType: 'xlsx',
                        type: 'array'
                    });
                    const blob = new Blob([wbout], {
                        type:
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });
                    this.downloadBlob(blob, nomeArquivo);
                }

                console.log('Arquivo Excel gerado:', nomeArquivo);
            } catch (error) {
                console.error('Erro ao gerar arquivo Excel:', error);
                this.showError('Erro ao gerar arquivo. Tente novamente.');
            }
        },

        aplicarProtecaoSimples(ws) {
            ws['!protect'] = {
                selectLockedCells: false,
                selectUnlockedCells: true,
                formatCells: false,
                formatColumns: false,
                formatRows: false,
                insertColumns: false,
                insertRows: false,
                deleteColumns: false,
                deleteRows: false,
                sort: false,
                autoFilter: false,
                pivotTables: false
            };

            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:L1000');

            for (let row = range.s.r; row <= range.e.r; row++) {
                for (let col = range.s.c; col <= range.e.c; col++) {
                    const cellAddress = XLSX.utils.encode_cell({
                        r: row,
                        c: col
                    });

                    if (!ws[cellAddress]) {
                        ws[cellAddress] = { t: 's', v: '' };
                    }

                    if (!ws[cellAddress].s) {
                        ws[cellAddress].s = {};
                    }

                    if (col === 0 || col === 1) {
                        ws[cellAddress].s.protection = { locked: true };
                    } else {
                        ws[cellAddress].s.protection = { locked: false };
                    }
                }
            }
        },

        downloadBlob(blob, filename) {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        },

        gerarNomeArquivo() {
            const agora = new Date();
            const data = agora
                .toISOString()
                .slice(0, 10)
                .replace(/-/g, '');
            const hora = agora
                .toTimeString()
                .slice(0, 8)
                .replace(/:/g, '');
            return `modelo_codevedores_${data}_${hora}.xlsx`;
        },

        processarDadosPlanilha() {
            const dados = [];

            dados.push([
                'ID TITULO',
                'NOSSO NUMERO',
                'CD_APRESENTANTE',
                'NOME DEVEDOR',
                'CPF/CNPJ',
                'ENDERECO',
                'BAIRRO',
                'CIDADE',
                'UF',
                'CEP',
                'EMAIL',
                'TELEFONE',
                'PRINCIPAL'
            ]);

            // Processar cada título selecionado
            this.titulosSelecionados.forEach(titulo => {
                const quantidadeDevedores = titulo.nu_quantidade_devedores || 1;
                const devedoresCadastrados = titulo.devedores || [];

                devedoresCadastrados.forEach(devedor => {
                    const linha = [];

                    linha.push(titulo.id_titulo);
                    linha.push(titulo.ds_nosso_numero);
                    linha.push(titulo.cd_apresentante || '');
                    linha.push(devedor.nm_devedor || '');
                    linha.push(
                        this.formatarDocumento(
                            devedor.nu_identificacao_devedor
                        ) || ''
                    );
                    linha.push(devedor.ds_endereco_devedor || '');
                    linha.push(devedor.ds_bairro_devedor || '');
                    linha.push(devedor.nm_cidade_devedor || '');
                    linha.push(devedor.cd_uf_devedor || '');
                    linha.push(devedor.cd_cep_devedor || '');
                    linha.push(devedor.ds_email_devedor || '');
                    linha.push(devedor.nu_telefone_devedor || '');
                    linha.push(devedor.fl_devedor_principal ? 1 : 0); // Principal = 1, co-devedor = 0

                    dados.push(linha);
                });

                const devedoresRestantes =
                    quantidadeDevedores - devedoresCadastrados.length;
                for (let i = 0; i < devedoresRestantes; i++) {
                    const linha = [];

                    linha.push(titulo.id_titulo);
                    linha.push(titulo.ds_nosso_numero);
                    linha.push(titulo.cd_apresentante || '');
                    linha.push(''); // NOME DEVEDOR
                    linha.push(''); // CPF/CNPJ
                    linha.push(''); // ENDERECO
                    linha.push(''); // BAIRRO
                    linha.push(''); // CIDADE
                    linha.push(''); // UF
                    linha.push(''); // CEP
                    linha.push(''); // EMAIL
                    linha.push(''); // TELEFONE
                    linha.push(0); // Principal = 0 (co-devedor)

                    dados.push(linha);
                }
            });

            return dados;
        },

        formatarDocumento(documento) {
            if (!documento) return 'N/A';

            const limpo = documento.replace(/[^a-zA-Z0-9]/g, '');

            if (limpo.length === 11) {
                return limpo.replace(
                    /(\d{3})(\d{3})(\d{3})(\d{2})/,
                    '$1.$2.$3-$4'
                );
            } else if (limpo.length === 14) {
                return limpo.replace(
                    /^(.{2})(.{3})(.{3})(.{4})(.{2})$/,
                    '$1.$2.$3/$4-$5'
                );
            }

            return documento;
        },

        getStatusColor(status, revisao, item) {
            if (item && item.mensagem_aviso) return 'red';

            if (revisao && revisao.includes('QD')) return 'purple';
            if (status === 220) return 'green';
            return 'grey';
        },

        getStatusText(status, revisao, item) {
            if (item && item.mensagem_aviso) return 'Inválido';

            if (revisao && revisao.includes('QD')) return 'QD';
            return `${status}`;
        },

        onToggleSelectAll(props) {
            if (props.value) {
                this.titulosSelecionados = this.titulosElegiveis;
            } else {
                this.titulosSelecionados = [];
            }
        },

        refazerBusca() {
            if (this.podeBuscar) {
                this.buscarTitulos();
            }
        },

        showError(message) {
            this.$emit('show-error', message);
        }
    }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper tbody tr:nth-child(odd) {
    background-color: #fafafa;
}

.v-chip--small {
    height: 24px !important;
}
</style>
